/**
 * Pure geometry/scale helpers for `UsageChart`, kept out of the component
 * so the component itself stays under the 200-line limit.
 */

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  /** Geometry values -- always a real number, never null (0 is used for
   * "no billable spend that day", which is a correct height, not a
   * display claim). Used only for drawing the stacked area. */
  values: number[];
  /** The true, possibly-null per-day value. Used for the tooltip and any
   * other text label, so a local-only day (`null`) never gets formatted
   * as "$0.00". */
  raw: Array<number | null>;
}

export interface StackedBand {
  series: ChartSeries;
  lower: number[];
  upper: number[];
}

/** Stacks series bottom-to-top in the order given. */
export function stackSeries(series: ChartSeries[]): StackedBand[] {
  const length = series[0]?.values.length ?? 0;
  let cumulative = new Array<number>(length).fill(0);
  return series.map((s) => {
    const lower = [...cumulative];
    const upper = cumulative.map((v, i) => v + (s.values[i] ?? 0));
    cumulative = upper;
    return { series: s, lower, upper };
  });
}

export interface PlotGeometry {
  /** X coordinate of each drawn vertex, left to right. */
  xs: number[];
  /** The series value belonging to vertex `i`. */
  valueAt: (values: number[], index: number) => number;
  /** Width of one day's hover target. */
  bandWidth: number;
  /** Left edge of the hover target for point `i`. */
  hoverX: (index: number) => number;
  /** Centre of the hover target for point `i` -- where the crosshair sits. */
  centreX: (index: number) => number;
}

/**
 * Where to draw, for `count` points across a plot `width` wide starting at
 * `left`.
 *
 * The single-point case is the reason this exists. Spreading one point
 * across `count - 1` intervals puts every vertex at the same x: the
 * stacked-area polygon then encloses zero area and the polyline has one
 * vertex, so the chart paints *nothing* while happily reporting it has
 * data. A lone day is instead drawn as a flat band across the full plot
 * -- "this is the level for the one day we have" -- which is visible and
 * honest, since there is no second day to slope towards.
 */
export function plotGeometry(count: number, left: number, width: number): PlotGeometry {
  if (count === 1) {
    return {
      xs: [left, left + width],
      valueAt: (values) => values[0] ?? 0,
      bandWidth: width,
      hoverX: () => left,
      centreX: () => left + width / 2,
    };
  }
  const step = width / Math.max(count - 1, 1);
  return {
    xs: Array.from({ length: count }, (_, i) => left + step * i),
    valueAt: (values, index) => values[index] ?? 0,
    bandWidth: step,
    hoverX: (index) => left + step * index - step / 2,
    centreX: (index) => left + step * index,
  };
}

/** Rounds a max value up to a "nice" gridline ceiling (1/2/5 * 10^n). */
export function niceMax(max: number): number {
  if (max <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  let step = magnitude;
  if (max / step < 2) step /= 2;
  else if (max / step > 5) step *= 2;
  return Math.ceil(max / step) * step;
}

/** Evenly spaced gridline values from 0 to `max` (inclusive). */
export function gridlines(max: number, count = 4): number[] {
  const step = max / count;
  const out: number[] = [];
  for (let v = 0; v <= max + 1e-9; v += step) out.push(v);
  return out;
}

export function formatDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
